import { useEffect } from "react";

const SELECTOR = "[data-reveal]:not([data-revealed]), [data-ray]:not([data-revealed])";

/**
 * One observer for the whole app: marks `[data-reveal]` / `[data-ray]`
 * elements as revealed the first time they enter the viewport. The flag is a
 * data attribute React never writes, so re-renders keep elements visible.
 */
export function RevealManager() {
  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      document.documentElement.classList.add("reveal-all");
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          (entry.target as HTMLElement).dataset.revealed = "true";
          io.unobserve(entry.target);
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 },
    );

    const scan = (root: ParentNode) => {
      root.querySelectorAll<HTMLElement>(SELECTOR).forEach((el) => io.observe(el));
    };
    scan(document);

    let queued = false;
    const mo = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        scan(document);
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);

  return null;
}
